import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { UserFilters } from 'stream-chat';
import { useChatContext } from 'stream-chat-react';
import z from 'zod';
import { EditButton, MutationButton } from '~/components/utils/buttons';
import { useStreamChatContext } from '~/react_context/StreamChatContext';
import StreamChatUserSelectList from './StreamChatUserSelectList';
import { trpc } from '@utils/trpc';
import { SingleInputForm } from '~/components/form-fields/SingleInputForm';
import { ErrorMessage } from '~/components/form-fields/ErrorMessage';

const formSchema = z.object({
	channelName: z
		.string()
		.min(2, 'Channel name must be at least 2 characters')
		.regex(/^[\w!\-_]+$/, 'Channel name can only contain letters, numbers or "!-_"'),
});
type FormType = z.infer<typeof formSchema>;

export default function EditChannelForm() {
	const { closeChatOverlay } = useStreamChatContext();
	const { channel, client } = useChatContext();
	const deleteChannelMutation = trpc.streamChat.disableChannel.useMutation();
	const [isEditingChannelName, setIsEditingChannelName] = useState(false);
	const {
		formState: { errors },
		handleSubmit,
		register,
		reset,
	} = useForm<FormType>({
		defaultValues: {
			channelName: channel?.data?.name,
		},
		resolver: zodResolver(formSchema),
	});
	const usersFilter = useMemo<UserFilters>(() => {
		const existingChannelMemberUserIds = channel ? Object.keys(channel.state.members) : [];
		return {
			id: { $in: [client.userID!, ...existingChannelMemberUserIds] },
		};
	}, [channel, client]);
	const channelData = channel?.data;

	if (!channelData) return null;

	const onSubmit = handleSubmit(async (data) => {
		await channel?.updatePartial({ set: { name: data.channelName } });

		closeChatOverlay();
	}, console.warn);

	const confirmDeleteChannel = async () => {
		if (confirm(`Do you really want to delete "${channelData.name ?? channelData.id ?? 'unknown-channel'}"?`)) {
			await deleteChannelMutation.mutateAsync({
				channelId: channelData.id!,
			});
			closeChatOverlay();
		}
	};

	return (
		<div className="h-full">
			<h1 className="text-lg mb-4">Channel Details</h1>
			<div className="overflow-y-auto h-[calc(100%-100px)]">
				{isEditingChannelName ? (
					<>
						<SingleInputForm
							id="edit-channel-name-form"
							mutation={undefined}
							name="channelName"
							register={register}
							onSubmit={onSubmit}
							onCancel={() => {
								reset();
								setIsEditingChannelName(false);
							}}
						/>
						<ErrorMessage id="edit-channel-name-error" errorMessage={errors?.channelName?.message} />
					</>
				) : (
					<h2 className="mb-1 font-medium">
						{channelData.name ?? channelData.id}
						<EditButton id="edit-channel-name-button" onClick={() => setIsEditingChannelName(true)} />
					</h2>
				)}
				<div className="my-4">
					<h2 className="mb-1 font-medium">Channel Members</h2>
					<StreamChatUserSelectList userFilter={usersFilter} />
				</div>
			</div>
			<div className="my-4 flex items-center justify-between">
				<button
					type="button"
					className="rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-xs hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-lg"
					onClick={closeChatOverlay}
				>
					Cancel
				</button>
				<MutationButton
					className="bg-red-600 hover:bg-red-800 text-white rounded-md border border-gray-300 px-4 py-2 text-base font-medium text-gray-700 shadow-xs hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-lg"
					mutation={deleteChannelMutation}
					onClick={confirmDeleteChannel}
					type="button"
				>
					Delete Channel
				</MutationButton>
			</div>
		</div>
	);
}
